import { useCallback, useEffect, useRef, useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import { callFunction } from '../../lib/api';

const KEY = 'pa_chat';
const readKey = () => { try { return localStorage.getItem(KEY) || ''; } catch { return ''; } };
const writeKey = (v) => { try { if (v) localStorage.setItem(KEY, v); else localStorage.removeItem(KEY); } catch { /* storage unavailable */ } };
const loadFs = () => Promise.all([import('firebase/firestore'), import('../../lib/db')]).then(([f, { db }]) => ({ ...f, db }));
export const openChat = () => window.dispatchEvent(new Event('pa:open-chat'));

/*
 * The visitor's side of the live chat. Firestore is only downloaded for people who have a
 * conversation (or open the chat), so it costs nothing for everyone else.
 */
export default function useSupportChat(open) {
  const { user, ensureSession } = useAuth();
  const [threadId, setThreadId] = useState(readKey);
  const [thread, setThread] = useState(null);
  const [messages, setMessages] = useState([]);
  const [pending, setPending] = useState([]);
  const [loading, setLoading] = useState(false);
  const marking = useRef(false);
  const uid = user?.uid;

  // Find an existing conversation for this account (e.g. a member on a new device).
  useEffect(() => {
    if (!open || !uid || threadId) return undefined;
    let live = true;
    setLoading(true);
    loadFs().then(({ collection, getDocs, query, where, db }) => getDocs(query(collection(db, 'support_threads'), where('owner_uid', '==', uid))))
      .then((snap) => {
        if (!live) return;
        const latest = snap.docs.map((d) => ({ id: d.id, ...d.data() }))
          .sort((a, b) => (b.last_message_at?.seconds || 0) - (a.last_message_at?.seconds || 0))[0];
        if (latest) { setThreadId(latest.id); writeKey(latest.id); }
      })
      .catch(() => {})
      .finally(() => { if (live) setLoading(false); });
    return () => { live = false; };
  }, [open, uid, threadId]);

  // Live conversation status (drives the unread dot even while the panel is closed).
  useEffect(() => {
    if (!threadId || !uid) return undefined;
    let unsub = () => {};
    let live = true;
    loadFs().then(({ doc, onSnapshot, db }) => {
      if (!live) return;
      unsub = onSnapshot(doc(db, 'support_threads', threadId),
        (s) => { if (s.exists()) setThread({ id: s.id, ...s.data() }); else { setThread(null); setThreadId(''); writeKey(''); } },
        () => { setThread(null); setThreadId(''); writeKey(''); }); // no longer ours (e.g. expired guest session)
    });
    return () => { live = false; unsub(); };
  }, [threadId, uid]);

  // Messages, only while the panel is open.
  useEffect(() => {
    if (!open || !threadId || !uid) return undefined;
    let unsub = () => {};
    let live = true;
    loadFs().then(({ collection, onSnapshot, orderBy, query, db }) => {
      if (!live) return;
      unsub = onSnapshot(query(collection(db, 'support_threads', threadId, 'messages'), orderBy('created_at', 'asc')),
        (s) => {
          setMessages(s.docs.map((d) => ({ id: d.id, ...d.data() })));
          setPending((p) => p.filter((x) => !s.docs.some((d) => d.data().text === x.text && d.data().from === 'user')));
        }, () => {});
    });
    return () => { live = false; unsub(); };
  }, [open, threadId, uid]);

  // Tell the server the visitor has seen new replies (clears the dot, and avoids emailing them).
  useEffect(() => {
    if (!open || !thread || !(thread.unread_user > 0) || marking.current) return;
    marking.current = true;
    callFunction('support_mark_read', { thread_id: thread.id }).catch(() => {}).finally(() => { marking.current = false; });
  }, [open, thread]);

  const start = useCallback(async ({ name, email, message, website }) => {
    await ensureSession();
    const res = await callFunction('support_start', { name, email, message, website, page: window.location.pathname });
    if (res.thread_id) { setThreadId(res.thread_id); writeKey(res.thread_id); }
    return res;
  }, [ensureSession]);

  const send = useCallback(async (text) => {
    const temp = { id: `tmp-${Date.now()}`, from: 'user', text, pending: true };
    setPending((p) => [...p, temp]);
    try {
      await callFunction('support_send', { thread_id: threadId, text });
    } catch (err) {
      setPending((p) => p.map((x) => (x.id === temp.id ? { ...x, failed: err.message } : x)));
      throw err;
    }
  }, [threadId]);

  const forget = useCallback(() => { writeKey(''); setThreadId(''); setThread(null); setMessages([]); }, []);

  return { threadId, thread, messages: [...messages, ...pending], loading, start, send, forget, unread: thread?.unread_user || 0 };
}
